import React, { useEffect, useState } from "react";
import { api, resolveMedia } from "@/lib/api";
import { SectionTitle } from "@/components/SectionTitle";
import { PlayCircle, X } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

function ytId(url) {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
  return m ? m[1] : null;
}

export default function Highlights() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/highlights").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const thumb = (h) => {
    if (h.thumbnail) return h.thumbnail;
    if (h.media_type === "image") return resolveMedia(h.url);
    if (h.media_type === "link") {
      const id = ytId(h.url);
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="highlights-page">
      <SectionTitle overline="Best of the league" title="Highlights" sub="Photos and videos capturing the biggest plays of the season." />

      {items.length === 0 && <p className="text-white/40">No highlights uploaded yet.</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((h) => {
          const t = thumb(h);
          const isVideo = h.media_type === "video" || h.media_type === "link";
          return (
            <button
              key={h.id}
              data-testid="highlight-card"
              onClick={() => setActive(h)}
              className="group text-left border border-white/10 bg-[#121212] overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_16px_rgba(255,87,34,0.3)] transition-all duration-200"
            >
              <div className="relative aspect-video bg-black overflow-hidden">
                {t ? (
                  <img src={t} alt={h.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A]"><PlayCircle size={48} className="text-white/30" /></div>
                )}
                {isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                    <PlayCircle size={56} weight="fill" className="text-[#FF5722]" />
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl uppercase">{h.title}</h3>
                {h.description && <p className="text-white/50 text-sm mt-1 line-clamp-2">{h.description}</p>}
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl bg-[#0A0A0A] border-white/10 p-0 overflow-hidden">
          {active && (
            <div>
              <div className="aspect-video bg-black">
                {active.media_type === "image" && (
                  <img src={resolveMedia(active.url)} alt={active.title} className="w-full h-full object-contain" />
                )}
                {active.media_type === "video" && (
                  <video src={resolveMedia(active.url)} controls autoPlay className="w-full h-full" />
                )}
                {active.media_type === "link" && (
                  ytId(active.url) ? (
                    <iframe title={active.title} className="w-full h-full" src={`https://www.youtube.com/embed/${ytId(active.url)}?autoplay=1`} allow="autoplay; encrypted-media" allowFullScreen />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <a href={active.url} target="_blank" rel="noreferrer" className="text-[#FF5722] underline">Open video link</a>
                    </div>
                  )
                )}
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl uppercase">{active.title}</h3>
                {active.description && <p className="text-white/60 mt-2">{active.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
